import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Pencil, Trash2, Search, Package, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import ExportButton from "@/components/ExportButton";

const statusBadge = (available, minimum) => {
  if (available <= 0) return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">OUT OF STOCK</Badge>;
  if (available <= minimum) return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">LOW STOCK</Badge>;
  return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">AVAILABLE</Badge>;
};

const SummaryCard = ({ icon: Icon, label, value, tone }) => (
  <Card className="border-slate-200"><CardContent className="p-4 flex items-center gap-3">
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tone}`}><Icon className="w-5 h-5"/></div>
    <div><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="text-xl font-bold text-slate-900" style={{fontFamily:"Outfit"}}>{value}</div>
    </div>
  </CardContent></Card>
);

export default function EquipmentPage({ resource, title, moduleKey, categories, hasCondition = false, testidPrefix }) {
  const qc = useQueryClient();
  const { canWrite } = useAuth();
  const canEdit = canWrite(moduleKey);

  const [q, setQ] = useState("");
  const [cat, setCat] = useState("ALL");
  const [status, setStatus] = useState("ALL");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const { data = [], isLoading } = useQuery({
    queryKey: [resource],
    queryFn: async () => (await apiClient.get(`/${resource}`)).data,
  });

  const filtered = useMemo(() => {
    return data.filter((i) => {
      const matchQ = !q || i.name?.toLowerCase().includes(q.toLowerCase()) || i.category?.toLowerCase().includes(q.toLowerCase());
      const matchCat = cat === "ALL" || i.category === cat;
      const avail = Number(i.available || 0), min = Number(i.minimum || 0);
      let st = avail <= 0 ? "OUT" : avail <= min ? "LOW" : "OK";
      const matchStatus = status === "ALL" || status === st;
      return matchQ && matchCat && matchStatus;
    });
  }, [data, q, cat, status]);

  const totals = useMemo(() => {
    let total = 0, avail = 0, low = 0, out = 0;
    data.forEach((i) => {
      total += Number(i.total || 0); avail += Number(i.available || 0);
      const a = Number(i.available || 0), m = Number(i.minimum || 0);
      if (a <= 0) out++; else if (a <= m) low++;
    });
    return { total, avail, low, out };
  }, [data]);

  const mSave = useMutation({
    mutationFn: async (body) => {
      if (body.id) return (await apiClient.put(`/${resource}/${body.id}`, body)).data;
      return (await apiClient.post(`/${resource}`, body)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey:[resource] }); qc.invalidateQueries({queryKey:["dashboard-stats"]}); qc.invalidateQueries({queryKey:["recent-logs"]}); toast.success("Data disimpan"); setOpen(false); setEditing(null); },
    onError: (e) => toast.error(e.response?.data?.detail || "Gagal menyimpan"),
  });

  const mDel = useMutation({
    mutationFn: async (id) => (await apiClient.delete(`/${resource}/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey:[resource] }); qc.invalidateQueries({queryKey:["dashboard-stats"]}); toast.success("Data dihapus"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Gagal menghapus"),
  });

  const openNew = () => { setEditing({ name:"", category: categories[0], total:0, available:0, assigned:0, damaged:0, in_use:0, minimum:0, condition:"BAIK", last_inspection:"", expiry_date:"", notes:"" }); setOpen(true); };
  const openEdit = (row) => { setEditing({ ...row }); setOpen(true); };

  return (
    <div className="space-y-6" data-testid={`${testidPrefix}-page`}>
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" style={{fontFamily:"Outfit"}}>{title}</h1>
        <p className="text-sm text-slate-500 mt-1">Manajemen inventaris dan status ketersediaan.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard icon={Package} label="Total Item" value={data.length} tone="bg-slate-100 text-slate-700" />
        <SummaryCard icon={CheckCircle2} label="Total Stok" value={totals.total} tone="bg-emerald-100 text-emerald-700" />
        <SummaryCard icon={AlertTriangle} label="Stok Rendah" value={totals.low} tone="bg-amber-100 text-amber-700" />
        <SummaryCard icon={AlertTriangle} label="Habis" value={totals.out} tone="bg-red-100 text-red-700" />
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
            <div className="flex flex-col sm:flex-row gap-2 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input placeholder="Cari nama / kategori..." className="pl-9" value={q} onChange={(e)=>setQ(e.target.value)} data-testid={`${testidPrefix}-search`} />
              </div>
              <Select value={cat} onValueChange={setCat}>
                <SelectTrigger className="w-full sm:w-44" data-testid={`${testidPrefix}-filter-category`}><SelectValue placeholder="Kategori" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Semua Kategori</SelectItem>
                  {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="w-full sm:w-40" data-testid={`${testidPrefix}-filter-status`}><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Semua Status</SelectItem>
                  <SelectItem value="OK">Tersedia</SelectItem>
                  <SelectItem value="LOW">Stok Rendah</SelectItem>
                  <SelectItem value="OUT">Habis</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {canEdit && (
              <Button onClick={openNew} className="bg-[#00A651] hover:bg-[#008741]" data-testid={`${testidPrefix}-add-btn`}>
                <Plus className="w-4 h-4 mr-1"/> Tambah Item
              </Button>
            )}
            <ExportButton rows={filtered} columns={[
              {key:"name",label:"Nama"},{key:"category",label:"Kategori"},{key:"total",label:"Total"},
              {key:"available",label:"Tersedia"},{key: hasCondition ? "in_use" : "assigned", label: hasCondition ? "Dipakai" : "Dipinjam"},
              {key:"damaged",label:"Rusak"},{key:"minimum",label:"Min"},
              ...(hasCondition ? [{key:"condition",label:"Kondisi"}] : []),
            ]} title={title} filename={testidPrefix} testid={`${testidPrefix}-export-btn`}/>
          </div>

          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <Table className="fodm-table">
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead>Nama</TableHead>
                  <TableHead>Kategori</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Tersedia</TableHead>
                  {hasCondition ? <TableHead className="text-right">Dipakai</TableHead> : <TableHead className="text-right">Dipinjam</TableHead>}
                  <TableHead className="text-right">Rusak</TableHead>
                  <TableHead className="text-right">Min</TableHead>
                  {hasCondition && <TableHead>Kondisi</TableHead>}
                  <TableHead>Status</TableHead>
                  <TableHead>Update</TableHead>
                  {canEdit && <TableHead className="text-right">Aksi</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? Array.from({length:5}).map((_,i)=>(
                  <TableRow key={i}><TableCell colSpan={canEdit?10:9}><Skeleton className="h-6 w-full"/></TableCell></TableRow>
                )) : filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={canEdit?10:9} className="text-center py-8 text-sm text-slate-500 italic">Tidak ada data.</TableCell></TableRow>
                ) : filtered.map((r) => (
                  <TableRow key={r.id} data-testid={`${testidPrefix}-row`}>
                    <TableCell className="font-medium text-slate-800">{r.name}</TableCell>
                    <TableCell><Badge variant="outline" className="font-normal">{r.category}</Badge></TableCell>
                    <TableCell className="text-right">{r.total}</TableCell>
                    <TableCell className="text-right font-semibold text-[#00A651]">{r.available}</TableCell>
                    <TableCell className="text-right">{hasCondition ? (r.in_use ?? 0) : (r.assigned ?? 0)}</TableCell>
                    <TableCell className="text-right text-red-600">{r.damaged || 0}</TableCell>
                    <TableCell className="text-right text-slate-500">{r.minimum}</TableCell>
                    {hasCondition && <TableCell><Badge className={r.condition === "BAIK" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>{r.condition || "BAIK"}</Badge></TableCell>}
                    <TableCell>{statusBadge(r.available, r.minimum)}</TableCell>
                    <TableCell className="text-xs text-slate-500">{r.updated_at ? new Date(r.updated_at).toLocaleDateString("id-ID") : "—"}</TableCell>
                    {canEdit && (
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="ghost" onClick={()=>openEdit(r)} data-testid={`${testidPrefix}-edit`}><Pencil className="w-4 h-4"/></Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild><Button size="sm" variant="ghost" className="text-red-600"><Trash2 className="w-4 h-4"/></Button></AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader><AlertDialogTitle>Hapus Item?</AlertDialogTitle><AlertDialogDescription>Data <b>{r.name}</b> akan dihapus permanen dari database.</AlertDialogDescription></AlertDialogHeader>
                              <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={()=>mDel.mutate(r.id)} data-testid={`${testidPrefix}-confirm-delete`}>Hapus</AlertDialogAction></AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing?.id ? "Ubah" : "Tambah"} Item</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Nama Item</Label><Input value={editing.name} onChange={(e)=>setEditing({...editing, name:e.target.value})} data-testid={`${testidPrefix}-form-name`}/></div>
              <div><Label>Kategori</Label>
                <Select value={editing.category} onValueChange={(v)=>setEditing({...editing, category:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>{categories.map(c=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Total</Label><Input type="number" value={editing.total} onChange={(e)=>setEditing({...editing, total:Number(e.target.value)})}/></div>
              <div><Label>Tersedia</Label><Input type="number" value={editing.available} onChange={(e)=>setEditing({...editing, available:Number(e.target.value)})}/></div>
              <div><Label>{hasCondition ? "Dipakai" : "Dipinjam"}</Label><Input type="number" value={hasCondition ? (editing.in_use||0) : (editing.assigned||0)} onChange={(e)=> hasCondition ? setEditing({...editing, in_use:Number(e.target.value)}) : setEditing({...editing, assigned:Number(e.target.value)}) }/></div>
              <div><Label>Rusak</Label><Input type="number" value={editing.damaged||0} onChange={(e)=>setEditing({...editing, damaged:Number(e.target.value)})}/></div>
              <div><Label>Minimum Stok</Label><Input type="number" value={editing.minimum} onChange={(e)=>setEditing({...editing, minimum:Number(e.target.value)})}/></div>
              {hasCondition && (
                <>
                  <div><Label>Kondisi</Label>
                    <Select value={editing.condition || "BAIK"} onValueChange={(v)=>setEditing({...editing, condition:v})}>
                      <SelectTrigger><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="BAIK">BAIK</SelectItem>
                        <SelectItem value="PERLU_KALIBRASI">PERLU KALIBRASI</SelectItem>
                        <SelectItem value="RUSAK">RUSAK</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Inspeksi Terakhir</Label><Input type="date" value={editing.last_inspection || ""} onChange={(e)=>setEditing({...editing, last_inspection:e.target.value})}/></div>
                  <div><Label>Kadaluarsa (opsional)</Label><Input type="date" value={editing.expiry_date || ""} onChange={(e)=>setEditing({...editing, expiry_date:e.target.value})}/></div>
                </>
              )}
              <div className="col-span-2"><Label>Catatan</Label><Input value={editing.notes || ""} onChange={(e)=>setEditing({...editing, notes:e.target.value})}/></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Batal</Button>
            <Button className="bg-[#00A651] hover:bg-[#008741]" onClick={()=>mSave.mutate(editing)} disabled={mSave.isPending} data-testid={`${testidPrefix}-form-save`}>Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
